import { DragEvent, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { IconKind } from 'app/frontend/types/custom/VisualPlan'
import { IconKindItem, iconKinds } from '../iconKinds'
import { hideSidebarTooltips, setSidebarDragData } from '../sidebarDrag'

type Props = {
  kind: IconKind
  items?: IconKindItem[]
  onAdd: (kind: IconKind, value: string) => void
}

const IconsToolbar = ({ kind, items, onAdd }: Props) => {
  const config = iconKinds[kind]
  const bare = config.toolbarFramed === false
  const columns = config.toolbarColumns ?? 5
  const resolvedItems = items ?? config.items
  const useWowhead = kind === 'ability'
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!dragging) return
    const clear = () => setDragging(false)
    window.addEventListener('dragend', clear)
    return () => window.removeEventListener('dragend', clear)
  }, [dragging])

  const startDrag = (event: DragEvent, value: string) => {
    setDragging(true)
    hideSidebarTooltips()
    setSidebarDragData(event.dataTransfer, {
      kind: 'icon',
      iconKind: kind,
      value,
    })
  }

  const tooltipOpen = dragging ? false : undefined

  if (resolvedItems.length === 0) return null

  return (
    <div className='flex flex-col gap-2'>
      <div className='text-xs font-medium text-muted-foreground'>
        {config.label}
      </div>
      <div
        className='grid gap-1'
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {resolvedItems.map((item) => {
          const icon = config.renderToolbarIcon(item.value)

          if (useWowhead) {
            return (
              <a
                key={item.value}
                href={`https://wowhead.com/spell=${item.value}`}
                target='_blank'
                rel='noreferrer'
                aria-label={item.label}
                className='flex h-auto w-full items-center justify-center py-0.5'
                draggable
                onDragStart={(event) => startDrag(event, item.value)}
                onClick={(event) => {
                  event.preventDefault()
                  onAdd(kind, item.value)
                }}
              >
                {icon}
              </a>
            )
          }

          if (bare) {
            return (
              <Tooltip key={item.value} open={tooltipOpen} delayDuration={300}>
                <TooltipTrigger asChild>
                  <button
                    type='button'
                    className='flex h-auto w-full items-center justify-center py-0.5'
                    aria-label={item.label}
                    draggable
                    onDragStart={(event) => startDrag(event, item.value)}
                    onClick={() => onAdd(kind, item.value)}
                  >
                    {icon}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{item.label}</TooltipContent>
              </Tooltip>
            )
          }

          return (
            <Tooltip key={item.value} open={tooltipOpen} delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  className='w-full h-9'
                  draggable
                  onDragStart={(event) => startDrag(event, item.value)}
                  onClick={() => onAdd(kind, item.value)}
                >
                  {icon}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{item.label}</TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}

export default IconsToolbar
